import { useEffect, useMemo, useRef, useState } from 'react'

import Slider from 'components/OfferCard/Slider'
import { GAME_CARD_OFFER_TYPE, OFFER_QUALITY } from 'constants/index'
import { formatNumber } from 'utils/utils'

import ReferFriend from '../ReferFriend'
import useOfferClick from '../useOfferClick'
import useOfferImpression, { offerTileData } from './useOfferImpression'
import { getOfferMilestones, isReferralOffer, piggyBanksToDollars } from './offerRewards'

/** The media strip of a featured card at full size, in css pixels. */
const MEDIA_HEIGHT = 120

/**
 * Page scroll, in css pixels, that takes the cards from full size to their minimum: the
 * difference between the two card heights in index.css (176px and 113px), so the bottom of
 * the cards follows the list underneath.
 */
const SHRINK_DISTANCE = 63

/**
 * Only the offers the backend actually marked as featured belong in this row.
 *
 * The marker is read instead of slicing the first few entries, so the row stays correct
 * whatever length featuredRow arrives with.
 */
export const selectFeaturedOffers = (offers = []) =>
  offers.filter((offer) => offer.offerQuality === OFFER_QUALITY.FEATURED.name)

const Pig = ({ size = 17 }) => (
  <img
    className="gamesRedesignPig"
    src="/images/pig.png"
    alt="piggy banks"
    width={size}
    height={size}
  />
)

const FeaturedCard = ({
  offer,
  index,
  observer,
  experiment,
  isOnboardingTarget,
  isAutoSwiping,
}) => {
  const { ref: impressionRef, guid: offerTileViewGUID } = useOfferImpression(observer)

  const { handleClick, isDisabled } = useOfferClick({
    offer,
    index,
    type: GAME_CARD_OFFER_TYPE.FEATURED,
    offerTileViewGUID,
    experiment,
  })

  const milestones = useMemo(() => getOfferMilestones(offer), [offer])
  const totalPiggyBanks = milestones.reduce((total, milestone) => total + milestone.piggyBanks, 0)

  return (
    <div
      ref={impressionRef}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleClick()
        }
      }}
      data-offer={offerTileData({ offer, index, guid: offerTileViewGUID, isFeatured: true })}
      className={`gamesListCard gamesRedesignFeaturedCard ${isDisabled ? 'disabled' : ''} ${
        isOnboardingTarget ? 'gamesRedesignOnboardingTarget' : ''
      }`}
    >
      <div className="gamesRedesignFeaturedMedia">
        <Slider
          game={offer}
          isAutoSwiping={isAutoSwiping}
          mediaHeight={MEDIA_HEIGHT}
          storageKey={`video-${offer.id}`}
          // A swipe on the card moves the carousel; the media keeps rotating on its own.
          isSwipeable={false}
        />
        <div className="gamesRedesignFeaturedScrim" />
        <span className="gamesRedesignFeaturedBadge">Exclusive for you</span>
        <div className="gamesRedesignFeaturedHeading">
          <span className="gamesRedesignFeaturedName">{offer.gameName}</span>
          {milestones.length > 0 && (
            <span className="gamesRedesignFeaturedSubtitle">
              Across {milestones.length} milestone{milestones.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {totalPiggyBanks > 0 && (
          // The full value box below says the same to screen readers.
          <span className="gamesRedesignFeaturedValueCompact" aria-hidden="true">
            <span className="gamesRedesignFeaturedValueCompactLabel">up to</span>
            {formatNumber(totalPiggyBanks)}
            <Pig size={14} />
          </span>
        )}
        {totalPiggyBanks > 0 && (
          <div className="gamesRedesignFeaturedValue">
            <span className="gamesRedesignFeaturedValueLabel">up to</span>
            <span className="gamesRedesignFeaturedValueAmount">
              {formatNumber(totalPiggyBanks)}
              <Pig size={16} />
            </span>
            <span className="gamesRedesignFeaturedValueDollars">
              ≈ ${piggyBanksToDollars(totalPiggyBanks).toFixed(2)}
            </span>
          </div>
        )}
      </div>

      <div className="gamesRedesignFeaturedBody">
        {/* Not a <button>: the whole card is the control, a nested one would click twice. */}
        <span className="gamesRedesignFeaturedCta">Start Earning!</span>
      </div>
    </div>
  )
}

/**
 * The part of the viewport where the carousel counts as seen: clear of the header above and
 * the tab bar below, the same allowance the legacy checkVisible used.
 */
const VISIBLE_AREA = '-60px 0px -70px 0px'

const Featured = ({ offers, observer, variant, experiment }) => {
  const sectionRef = useRef(null)
  const trackRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isOnScreen, setOnScreen] = useState(false)

  // Shrinks the cards as the page scrolls. Written to css variables, not state, so scrolling
  // never re-renders the carousel.
  useEffect(() => {
    const section = sectionRef.current
    // With no Featured cards, All offers still needs to stick under the header and banners.
    const page = section?.parentElement ?? document.querySelector('.gamesRedesign')
    if (!page) {
      return
    }

    const header = document.querySelector('.gamesHeader')
    const banners = document.querySelector('.gamesRedesignTopBanners')
    let frame = null

    const update = () => {
      frame = null
      const progress = Math.min(Math.max(window.scrollY / SHRINK_DISTANCE, 0), 1)
      // Featured sticks under the header and whatever top banner is showing.
      const headerHeight = header?.offsetHeight ?? 0
      const top = headerHeight + (banners?.offsetHeight ?? 0)
      section?.style.setProperty('--featured-progress', progress.toFixed(3))
      section?.style.setProperty('--featured-top', `${top}px`)
      banners?.style.setProperty('--games-header-height', `${headerHeight}px`)

      // The All offers header sticks right under Featured, and shows a shadow once it does.
      const allOffersTop = top + (section?.offsetHeight ?? 0)
      page.style.setProperty('--all-offers-top', `${allOffersTop}px`)
      const allOffersHeader = page.querySelector('.gamesRedesignAllOffersHeader')
      allOffersHeader?.classList.toggle(
        'stuck',
        allOffersHeader.getBoundingClientRect().top <= allOffersTop + 0.5
      )
    }
    const scheduleUpdate = () => {
      if (frame === null) {
        frame = requestAnimationFrame(update)
      }
    }

    update()
    window.addEventListener('scroll', scheduleUpdate, { passive: true })
    window.addEventListener('resize', scheduleUpdate)
    // A top banner opening or closing moves where Featured sticks.
    const bannersObserver = window.ResizeObserver ? new ResizeObserver(scheduleUpdate) : null
    if (banners) {
      bannersObserver?.observe(banners)
    }

    return () => {
      bannersObserver?.disconnect()
      window.removeEventListener('scroll', scheduleUpdate)
      window.removeEventListener('resize', scheduleUpdate)
      if (frame !== null) {
        cancelAnimationFrame(frame)
      }
    }
  }, [offers])

  useEffect(() => {
    const track = trackRef.current
    if (!track) {
      return
    }

    const visibility = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), {
      rootMargin: VISIBLE_AREA,
    })
    visibility.observe(track)

    return () => visibility.disconnect()
  }, [offers])

  useEffect(() => {
    const track = trackRef.current
    if (!track) {
      return
    }

    const onScroll = () => {
      const card = track.firstElementChild
      if (!card) {
        return
      }
      const step = card.offsetWidth + parseFloat(getComputedStyle(track).columnGap || 0)
      setActiveIndex(Math.round(track.scrollLeft / step))
    }

    track.addEventListener('scroll', onScroll, { passive: true })

    return () => track.removeEventListener('scroll', onScroll)
  }, [offers])

  if (!offers?.length) {
    return null
  }

  const onboardingTargetIndex = offers.findIndex((offer) => !isReferralOffer(offer))

  return (
    <section
      className="gamesRedesignFeatured"
      aria-label="Featured"
      data-variant={variant}
      ref={sectionRef}
    >
      <header className="gamesRedesignSectionHeader">
        <h2 className="gamesRedesignSectionTitle">Featured</h2>
        <span className="gamesRedesignSectionAside">Picked for you</span>
      </header>
      <div className="gamesRedesignFeaturedTrack" data-testid="featured-track" ref={trackRef}>
        {offers.map((offer, index) =>
          isReferralOffer(offer) ? (
            <div className="gamesRedesignFeaturedReferral" key={offer.id}>
              <ReferFriend referralOffer={offer} experiment={experiment} />
            </div>
          ) : (
            <FeaturedCard
              key={offer.id}
              offer={offer}
              index={index}
              observer={observer}
              experiment={experiment}
              isOnboardingTarget={index === onboardingTargetIndex}
              isAutoSwiping={isOnScreen && index === activeIndex}
            />
          )
        )}
      </div>
      {offers.length > 1 && (
        <div className="gamesRedesignFeaturedDots" aria-hidden>
          {offers.map((offer, index) => (
            <span
              key={offer.id}
              className={`gamesRedesignFeaturedDot ${index === activeIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default Featured
